import * as React from 'react';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FullPageEditor} from 'shells/layout/full-page-editor';

import type {LandingPageFullFragment} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {BeeEditorLandingPages} from '../bee-editor/landing-page-editor';
import {useDiscardChanges, usePublishPage} from '../graphql/hooks';

type Props = {
    page: LandingPageFullFragment;
    onUpdatePage: (beeContentJson: string) => Promise<any>;
};

export function LandingPageEditor(props: Props) {
    const [isConfirmingDiscard, setIsConfirmingDiscard] = React.useState<boolean>(false);

    const {publishPage, isPublishing, error: errorPublishing} = usePublishPage();
    const onPublishPage = () => {
        return publishPage(props.page.editableVersion.id);
    };

    const {discardChanges} = useDiscardChanges();

    const onClose = () => appHistory.push(`/email-marketing/landing-pages/${props.page.id}/main`);

    return (
        <>
            <FullPageEditor
                title={props.page.name}
                onBack={() => {
                    if (props.page.hasUnpublishedChanges) {
                        setIsConfirmingDiscard(true);
                    } else {
                        onClose();
                    }
                }}
                backButtonText={props.page.hasUnpublishedChanges ? 'Discard changes' : 'Back'}
                onSaveClick={() => {
                    onPublishPage().then(() => {
                        onClose();
                    });
                }}
                previewPath={`/preview/landing-pages/${props.page.id}`}
                previewButtonText='Preview this page'
                isSaveDisabled={!props.page.hasUnpublishedChanges}
                saveButtonText='Publish changes'
                savingText='Publishing…'
                successText='Published!'
                isSaving={isPublishing}
                errorSaving={Boolean(errorPublishing)}
                content={
                    <BeeEditorLandingPages
                        // @ts-expect-error
                        pageId={props.page.id}
                        beeBodyJson={
                            props.page.editableVersion
                                ? props.page.editableVersion.beeBodyJson
                                : undefined
                        }
                        onChange={(json) => {
                            try {
                                return props.onUpdatePage(json);
                            } catch {
                                // @ts-expect-error
                                window.trackJs.track('Landing page changes failed to save');
                            }
                        }}
                    />
                }
            />
            {isConfirmingDiscard ? (
                <ConfirmationDialog
                    title='Discard changes'
                    message='If you leave now, your changes will be discarded. Would you like to go back?'
                    confirmText='Yes, discard changes'
                    cancelText='No, stay here'
                    onCancel={() => {
                        setIsConfirmingDiscard(false);
                    }}
                    onConfirm={() => {
                        discardChanges(props.page.id).then(onClose);
                    }}
                    isConfirmDestructive={true}
                />
            ) : undefined}
        </>
    );
}
